import "./App.css";
import { Suspense, createContext, lazy, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Grid, Modal, Box, Typography } from "@mui/material";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
// import AudioManager from "./utils/AudioManager";
import { useSoundsStore } from "./@shared/store/useSoundsStore";
import { auth } from "../src/@shared/firebase/config"; // your firebase storage instance
import { signOut } from "firebase/auth";
import { useAuthStore } from "./@shared/store/useAuthStore";
import {
  clearStudioLaunchSessionQueryParam,
  exchangeStudioLaunchSession,
  getStudioLaunchSessionId,
} from "./@shared/auth/studioLaunchSession";
import { ThemeProvider, createTheme } from "@mui/material";
import { theme as Theme, uiMuiTheme, baseMuiTheme } from "./Theme";
import HomeScreen from "./screens/HomeScreen";
import SplashScreen from "./screens/SplashScreen";
import CardManager from "./components/CardManager";
import GradientExamples from "./GradientExamples";
import Test from "./screens/Test";
import MusicController from "./components/MusicController";

import playerHit from "./@shared/assets/sounds/anime-hit-sfx.mp3";
import doorTransition from "./@shared/assets/sounds/door-transition.mp3";
import buff1 from "./@shared/assets/sounds/sfx-buff.mp3";
import cardSlapPackOpen from "./@shared/assets/sounds/sfx-cards-woosh.mp3";
import cardSwooshIn from "./@shared/assets/sounds/sfx-swoosh-in.mp3";
import cardShuffle from "./@shared/assets/sounds/card-shuffle-sfx.mp3";
import cardSwooshOut from "./@shared/assets/sounds/sfx-swoosh-out.mp3";
import buttonPress from "./@shared/assets/sounds/sfx-menu-button-2.mp3";
import buttonPressNoGood from "./@shared/assets/sounds/sfx-menu-button-cancel.mp3";
import liveChat from "./@shared/assets/sounds/sfx-live-chat-2.mp3";
import tokensClaim from "./@shared/assets/sounds/sfx-tokens.mp3";
import AudioManager from "./@shared/utils/AudioManager";
import OnboardingScreen from "./screens/OnboardingScreen";
import TransitionOverlay from "./components/TransitionOverlay";
import LoadingScreen from "./components/LoadingScreen";
import "driver.js/dist/driver.css";
import ReferralLanding from "./screens/ReferralLanding";
import MailToastManager from "./components/MailToastManager";
import CollectionEnrollmentPage from "./components/CollectionEnrollmentPage";
import AppUpdatePrompt from "./components/AppUpdatePrompt";
import EnrollmentOverview from "./screens/EnrollmentOverview";
import { WagmiConfig } from 'wagmi';
import { createConfig, http } from '@wagmi/core';
import { injected } from '@wagmi/connectors';
import { abstract } from 'viem/chains';
import { AbstractWalletProvider } from '@abstract-foundation/agw-react';

const MyCardsScreen = lazy(() => import("./screens/MyCardsScreen"));
const MyDecksScreen = lazy(() => import("./screens/MyDecksScreen"));
const DeckBuilder = lazy(() => import("./screens/DeckBuilder"));
const GameBoardScreen = lazy(() => import("./screens/GameBoardScreen"));
const LobbyScreen = lazy(() => import("./screens/LobbyScreen"));
const StoreScreen = lazy(() => import("./screens/StoreScreen"));
const WorldScreen = lazy(() => import("./screens/WorldScreen"));
const AdminScreen = lazy(() => import("./screens/AdminScreen"));
const PlayerDashboard = lazy(() => import("./screens/PlayerDashboard"));
const MailModal = lazy(() => import("./components/MailModal"));
const MerchPage = lazy(() => import("./screens/MerchScreen"));
const HowToPlayScreen = lazy(() => import("./screens/HowToPlayScreen"));
const PolicyCenterScreen = lazy(() => import("./screens/PolicyCenterScreen"));
const UnsubscribeScreen = lazy(() => import("./screens/UnsubscribeScreen"));
const PublicCardRegistryScreen = lazy(() =>
  import("./screens/PublicCardRegistryScreen")
);

// const { chains, publicClient, webSocketPublicClient } = configureChains(
//   [abstractTestnet],
//   [publicProvider()]
// );

// const wagmiConfig = createConfig({
//   autoConnect: true,
//   chains: [abstractTestnet],
//   transports: {
//     [abstractTestnet.id]: http(),
//   },
//   connectors: [
//     injected({ chains: [abstractTestnet] }),
//   ],
// });

export const wagmiConfig = createConfig({
  autoConnect: true,
  chains: [abstract],
  transports: {
    [abstract.id]: http("https://api.mainnet.abs.xyz"),
  },
  connectors: [
    injected({ chains: [abstract] }),
  ],
});

export let MainContext = createContext({});

function App() {
  const { userData, initAuth } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [info, setInfo] = useState(false);
  const [message, setMessage] = useState("!");
  const [deviceType, setDeviceType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [partyId, setPartyId] = useState(null);
  const [isHost, setIsHost] = useState(null);
  const [screenLoading, setScreenLoading] = useState(false);
  const [gameScreenLoading, setGameScreenLoading] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [launchSessionLoading, setLaunchSessionLoading] = useState(true);
  // Account Setting Sections
  const [accountSettingsVisible, setAccountSettingsVisible] = useState(false);
    const [activeSection, setActiveSection] = useState("account"); // "account" | "integrations" | "orbs" | "notifications"

  useEffect(() => {
    if (location.pathname !== "/dashboard") return;

    const params = new URLSearchParams(location.search);
    if (params.get("settings") !== "notifications") return;

    setAccountSettingsVisible(true);
    setActiveSection("notifications");
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname !== "/auth") return;
    if (!userData?.id) return;

    const params = new URLSearchParams(location.search);
    const returnTo = String(params.get("returnTo") || "").trim();
    if (!returnTo.startsWith("/")) return;

    navigate(returnTo, { replace: true });
  }, [location.pathname, location.search, navigate, userData?.id]);

  const setSoundBank = useSoundsStore((s) => s.setSfxBankAndReady);
  const unlockedRef = useRef(false);

  const handleForceSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (err) {
      console.error("Forced sign-out failed:", err.message);
    }
  };

  useEffect(() => {
    const unlockAudio = () => {
      if (unlockedRef.current) return;
      // Simply create & play a silent buffer to unlock the audio context:
      const a = new Audio();
      a.volume = 0;
      a.play()
        .catch(() => { })
        .finally(() => {
          a.pause();
          unlockedRef.current = true;
        });
      document.removeEventListener("click", unlockAudio);
    };
    document.addEventListener("click", unlockAudio);
    return () => document.removeEventListener("click", unlockAudio);
  }, []);

  useEffect(() => {
    async function preloadAllAudio() {
      // 3a) Preload SFX
      await Promise.all([
        AudioManager.loadSFX("playerHit", playerHit),
        AudioManager.loadSFX("doorTransition", doorTransition),
        AudioManager.loadSFX("buff1", buff1),
        AudioManager.loadSFX("cardSlapPackOpen", cardSlapPackOpen),
        AudioManager.loadSFX("cardSwooshIn", cardSwooshIn),
        AudioManager.loadSFX("cardShuffle", cardShuffle),
        AudioManager.loadSFX("cardSwooshOut", cardSwooshOut),
        AudioManager.loadSFX("buttonPress", buttonPress),
        AudioManager.loadSFX("buttonPressNoGood", buttonPressNoGood),
        AudioManager.loadSFX("liveChat", liveChat),
        AudioManager.loadSFX("tokensClaim", tokensClaim),
      ]);

      setSoundBank(AudioManager.sfxBank);
    }

    preloadAllAudio().catch(console.error);
  }, [setSoundBank]);

  useEffect(() => {
    if (userData) return;
    initAuth();
  }, [initAuth, userData]);

  useEffect(() => {
    let active = true;

    async function hydrateStudioLaunchSession() {
      const sessionId = getStudioLaunchSessionId();

      if (!sessionId) {
        if (active) {
          setLaunchSessionLoading(false);
        }
        return;
      }

      try {
        await exchangeStudioLaunchSession(sessionId);
      } catch (error) {
        console.error("Studio launch session sign-in failed:", error);
      } finally {
        clearStudioLaunchSessionQueryParam();
        if (active) {
          setLaunchSessionLoading(false);
        }
      }
    }

    hydrateStudioLaunchSession();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, []);

  //useEffect to set device type
  useEffect(() => {
    if (window.innerWidth < 600) {
      setDeviceType("mobile");
    } else setDeviceType("desktop");
  }, []);


  const values = {
    error,
    setError,
    success,
    setSuccess,
    info,
    setInfo,
    message,
    setMessage,
    Theme,
    navigate,
    deviceType,
    partyId,
    setPartyId,
    isHost,
    setIsHost,
    screenLoading,
    setScreenLoading,
    gameScreenLoading,
    setGameScreenLoading,
    accountSettingsVisible,
    setAccountSettingsVisible,
    activeSection,
    setActiveSection
  };

  // Add inside the App component, right before return
  const driverStyles = `
  /* Override popover wrapper directly */
  .driver-popover {
    background-color: #ffffff !important; /* ✅ Purple background */
    // background-color: #6f52ed !important; /* ✅ Purple background */
    color: #000000 !important;
    border-radius: 12px !important;
    padding: 16px !important;
    box-shadow: 0 0 12px #6f52ed88 !important;
  }

  :not(body):has(>.driver-active-element) {
    overflow: visible !important;
  }

  .driverjs-theme .driver-popover-title {
    font-size: 1.25rem;
    font-weight: bold;
    color: #ffffff;
  }

  .driver-highlighted-element,
[data-driver].driver-highlighted-element,
.driver-highlighted-element * {
  outline: 3px dashed red !important;
   background-color: rgba(255, 0, 0, 0.2) !important;
  box-shadow: 0 0 0 3px red !important;
  z-index: 99999 !important;
  position: relative !important;
  overflow: visible !important;
}


  .driverjs-theme .driver-popover-description {
    font-size: 1rem;
    line-height: 1.4;
    color: #ffffff;
  }

  .driver-popover-footer button {
  display: none !important;
    background-color: #ffffff !important;
    color: #6f52ed !important;
    border-radius: 8px;
    padding: 6px 12px;
    font-weight: bold;
  }

  .driver-popover-footer button:hover {
  display: none !important;
    background-color: #dcd5ff !important;
  }

  .driver-popover-footer .driver-popover-progress-text {
  display: none !important;
  color: #ffcc00 !important;
}

/* 🔍 DEBUG: Show green outline around the opponent platform */
  [data-driver='opponent-platform'] {
     content: "";
	 overflow: visible !important;
  }
/* 🔍 DEBUG: Show green outline around the opponent platform */
 [data-driver='opponent-stats']::before {
  content: "";
  position: absolute;
  top: -10px;
  bottom: -10px;
  left: -20px;
  right: -20px;
  background: transparent;
  z-index: -1;
}

/* 🔍 DEBUG: Show green outline around the opponent platform */
 [data-driver='player-stats']::before {
  content: "";
  position: absolute;
  top: -10px;
  bottom: -10px;
  left: -20px;
  right: -20px;
  background: transparent;
  z-index: -1;
  
}

   /* 🔍 DEBUG: Show green outline around the opponent platform */
 [data-driver='action-button']::before {
  content: "";
  position: absolute;
  top: -10px;
  bottom: -10px;
  left: -20px;
  right: -20px;
  background: transparent;
  z-index: -1;
  
}

   /* 🔍 DEBUG: Show green outline around the opponent platform */
 [data-driver='accuracy-card']::before {
  content: "";
  offset: 40,
  
}


`

  return (
    <WagmiConfig config={wagmiConfig}>
      <AbstractWalletProvider chain={abstract}>
        <MainContext.Provider value={values}>
          <ThemeProvider
            theme={
              location.pathname.startsWith("/gameboard") ? baseMuiTheme : uiMuiTheme
            }
          >
            <style dangerouslySetInnerHTML={{ __html: driverStyles }} />
            <Grid
              item
              container
              xs={12}
              style={{
                height: "100dvh",
                width: "100dvw",
                overflowX: "hidden",
                justifyContent: "center",
              }}
            >
              <MusicController />

              {/* <Toast /> */}
              {isLoading || launchSessionLoading ? (
                <SplashScreen />
              ) : (
                <Grid item container xs={12} style={{ height: "100%" }}>
                  {/* <Grid item container xs={12} style={{ height: 64, zIndex: 2 }}>
                <MenuBar navigate={navigate} />
              </Grid> */}
                  {userData?.id && (
                    <MailToastManager
                      userId={userData.id}
                      openMailModal={() => setMailOpen(true)}
                      mailOpen={mailOpen}
                    />
                  )}
                  {userData?.id && <AppUpdatePrompt />}
                  <Grid
                    item
                    container
                    xs={12}
                    style={{ height: "100dvh", zIndex: 1 }}
                  // style={{ height: "calc(100dvh - 64px)", zIndex: 1 }}
                  >
                    <Suspense
                      fallback={
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress color="inherit" />
                        </Box>
                      }
                    >
                      <Routes>
                        <Route path="/" element={<HomeScreen />} />
                        <Route path="/dashboard" element={<PlayerDashboard />} />
                        <Route path="/how-to-play" element={<HowToPlayScreen />} />
                        <Route path="/world" element={<WorldScreen />} />
                        <Route path="/auth" element={<HomeScreen isAuth={true} />} />
                        <Route path="/cardmanager" element={<CardManager />} />
                        <Route path="/mycards" element={<MyCardsScreen />} />
                        <Route path="/mydecks" element={<MyDecksScreen />} />
                        <Route path="/deckbuilder" element={<DeckBuilder />} />
                        <Route
                          exact
                          path="/gameboard/:roomId/match"
                          element={<GameBoardScreen />}
                        />
                        <Route path="/lobby/:lobbyId" element={<LobbyScreen />} />
                        <Route path="/store" element={<StoreScreen />} />
                        <Route path="/merch" element={<MerchPage />} />
                        <Route path="/policy" element={<PolicyCenterScreen />} />
                        <Route path="/unsubscribe" element={<UnsubscribeScreen />} />
                        <Route
                          path="/card-registry"
                          element={<PublicCardRegistryScreen />}
                        />
                        <Route
                          path="/store/success/:productType"
                          element={<StoreScreen />}
                        />
                        <Route path="/test" element={<Test />} />
                        <Route path="/admin" element={<AdminScreen />} />
                        <Route path="/onboarding" element={<OnboardingScreen />} />
                        <Route path="/referral" element={<ReferralLanding />} />
                        <Route path="/enrollment" element={<EnrollmentOverview />} />
                        <Route
                          path="/enrollment/:enrollmentId"
                          element={<CollectionEnrollmentPage />}
                        />

                        <Route
                          path="/test"
                          element={
                            <Grid
                              item
                              container
                              xs={12}
                              style={{
                                height: "100%",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <GradientExamples />
                              {userData && <Button>Animate</Button>}
                            </Grid>
                          }
                        />
                      </Routes>
                    </Suspense>

                    {/* ✅ Always render this so it's available for screen transitions */}
                    <TransitionOverlay />
                    <LoadingScreen />
                    {/* GLOBAL MODAL THAT CAN BE USED FOR IMPORTANT MESSAGES */}
                    <Modal open={false}>
                      <Box
                        sx={{
                          backgroundColor: "#111",
                          color: "#fff",
                          maxWidth: 500,
                          mx: "auto",
                          my: "20vh",
                          p: 4,
                          borderRadius: 3,
                          boxShadow: 24,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                          🚀 InfiNFT Royale: Pre-Season is Heating Up!
                        </Typography>

                        <Typography sx={{ fontSize: 15, mb: 2 }}>
                          We're gearing up for the biggest launch in NFT card gaming
                          — and you're here early. That means more ways to stand out
                          and leave your mark.
                        </Typography>

                        <Typography sx={{ fontSize: 15, mb: 2 }}>
                          Make sure you’re ready. Stay updated. Get your cards in.
                          Support the launch. Let’s build something legendary —
                          together.
                        </Typography>

                        <Typography sx={{ fontSize: 15, mb: 3 }}>
                          If you're just exploring, that's cool too. Come back when
                          you're ready to join the fight.
                        </Typography>

                        <Button
                          variant="contained"
                          onClick={handleForceSignOut}
                          sx={{
                            bgcolor: "#6f52ed",
                            "&:hover": { bgcolor: "#5a3ed3" },
                            fontWeight: 700,
                            mt: 2,
                          }}
                        >
                          Confirm & Sign Out
                        </Button>
                      </Box>
                    </Modal>
                  </Grid>
                </Grid>
              )}

              {mailOpen && (
                <Suspense fallback={null}>
                  <MailModal
                    visible={mailOpen}
                    onClose={() => setMailOpen(false)}
                    userId={userData?.id}
                  />
                </Suspense>
              )}
            </Grid>
          </ThemeProvider>
        </MainContext.Provider>
      </AbstractWalletProvider>
    </WagmiConfig>
  );
}

export default App;
